// src/utils/ScannerContext.js
import React, { createContext, useContext, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const ScannerCtx = createContext(null);

export function ScannerProvider({ children }) {
  const navigate = useNavigate();

  const resolverRef = useRef(null);
  const prestartedStreamRef = useRef(null);
  const [pending, setPending] = useState(null); // holds { title } shown on scanner page

  // Promise that resolves when the scanner completes/cancels
  const open = useCallback((opts) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setPending(opts || { title: "Σάρωση" });
    });
  }, []);

  const complete = useCallback((value) => {
    try { resolverRef.current?.({ ok: true, value }); }
    finally {
      resolverRef.current = null;
      setPending(null);
    }
  }, []);

  const cancel = useCallback(() => {
    try { resolverRef.current?.({ ok: false }); }
    finally {
      resolverRef.current = null;
      setPending(null);
    }
  }, []);

  // Must be triggered by a user gesture to avoid mobile autoplay limits
  const preStartStream = useCallback(async (constraints) => {
    try { prestartedStreamRef.current?.getTracks?.().forEach(t => t.stop()); } catch {}
    prestartedStreamRef.current = await navigator.mediaDevices.getUserMedia(
      constraints || { video: { facingMode: { ideal: "environment" } }, audio: false }
    );
    return prestartedStreamRef.current;
  }, []);

  const takePrestartedStream = useCallback(() => {
    const s = prestartedStreamRef.current;
    prestartedStreamRef.current = null;
    return s;
  }, []);

  // High-level helper used by your UI (e.g., from QtyModal button)
  const requestScan = useCallback(async ({ title, parseFn, onResult } = {}) => {
    // 1) Start stream under the same click
    try { await preStartStream({ video: { facingMode: { ideal: "environment" } }, audio: false }); } catch {}

    // 2) Navigate to the full-screen scanner page
    navigate("/scan", { replace: false });

    // 3) Wait for the scan
    const res = await open({ title: title || "Σάρωση" });
    if (!res?.ok) return { ok: false };

    const raw = res.value ?? "";
    const parsed = typeof parseFn === "function" ? parseFn(raw) : raw;

    // 4) Invoke callback (if any)
    try { typeof onResult === "function" && onResult(parsed, raw); } catch {}

    return { ok: true, raw, parsed };
  }, [navigate, open, preStartStream]);

  return (
    <ScannerCtx.Provider
      value={{
        // low-level
        open, complete, cancel,
        preStartStream, takePrestartedStream,
        pending,
        // high-level
        requestScan,
      }}
    >
      {children}
    </ScannerCtx.Provider>
  );
}

export function useScanner() {
  const ctx = useContext(ScannerCtx);
  if (!ctx) throw new Error("useScanner must be used within <ScannerProvider>");
  return ctx;
}
